import { Head } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface EmployeePermission {
  id: number
  name: string
  initials: string | null
  permissions: Record<string, boolean>
  company_ids: number[]
}

interface CompanyInfo {
  id: number
  name: string
}

type PermissionCategories = Record<string, string[]>

interface Props {
  employees: EmployeePermission[]
  permission_categories: PermissionCategories
  companies: CompanyInfo[]
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Employees", href: "/employees" },
  { title: "Permission Matrix", href: "/employees/permissions" },
]

export default function Permissions({
  employees,
  permission_categories,
  companies,
}: Props) {
  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Permission Matrix" />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">Permission Matrix</h1>

        <div className="overflow-x-auto">
          <table className="border-hpi-light-gray min-w-full border-collapse border text-sm">
            <thead>
              <tr className="bg-hpi-pale-gray">
                <th className="border-hpi-light-gray bg-hpi-pale-gray sticky left-0 z-10 border px-3 py-2 text-left">
                  Permission
                </th>
                {employees.map((emp) => (
                  <th
                    key={emp.id}
                    className="border-hpi-light-gray border px-2 py-2 text-center"
                    title={emp.name}
                  >
                    {emp.initials ?? emp.name.substring(0, 3)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(permission_categories).map(
                ([category, permissions]) => (
                  <>
                    <tr key={`cat-${category}`}>
                      <td
                        colSpan={employees.length + 1}
                        className="border-hpi-light-gray bg-hpi-pale-gray text-hpi-dark-gray border px-3 py-1 font-semibold"
                      >
                        {category}
                      </td>
                    </tr>
                    {permissions.map((perm) => (
                      <tr key={perm} className="hover:bg-hpi-pale-gray">
                        <td className="border-hpi-light-gray sticky left-0 z-10 border bg-white px-3 py-1">
                          {perm}
                        </td>
                        {employees.map((emp) => (
                          <td
                            key={`${emp.id}-${perm}`}
                            className="border-hpi-light-gray border px-2 py-1 text-center"
                          >
                            {emp.permissions[perm] ? (
                              <span className="text-hpi-dark-green" title="Yes">
                                &#10003;
                              </span>
                            ) : (
                              <span className="text-hpi-med-gray">-</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </>
                ),
              )}
              {companies.length > 1 && (
                <>
                  <tr key="cat-companies">
                    <td
                      colSpan={employees.length + 1}
                      className="border-hpi-light-gray bg-hpi-pale-gray text-hpi-dark-gray border px-3 py-1 font-semibold"
                    >
                      Companies
                    </td>
                  </tr>
                  {companies.map((company) => (
                    <tr
                      key={`company-${company.id}`}
                      className="hover:bg-hpi-pale-gray"
                    >
                      <td className="border-hpi-light-gray sticky left-0 z-10 border bg-white px-3 py-1">
                        {company.name}
                      </td>
                      {employees.map((emp) => (
                        <td
                          key={`${emp.id}-company-${company.id}`}
                          className="border-hpi-light-gray border px-2 py-1 text-center"
                        >
                          {emp.company_ids.includes(company.id) ? (
                            <span className="text-hpi-dark-green" title="Yes">
                              &#10003;
                            </span>
                          ) : (
                            <span className="text-hpi-med-gray">-</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AppLayout>
  )
}
